import {
  assertCanAssignTaxonomyNode,
  assertProductActivationReadiness,
  buildNormalizedTaxonomyState,
  resolveAncestors,
  uniqueSortedIds,
} from '../../../productSchema/productTaxonomyFields'
import { buildProductTaxonomyWritePatch } from '../../../productSchema/buildProductTaxonomyWritePatch'
import { readProductTaxonomyFields } from '../../../productSchema/readProductTaxonomyFields'
import {
  applyAddExtraCategory,
  applySetMainCategory,
  partitionProductsForPrimaryCategorize,
  splitMainAndExtraAssignments,
} from '../../../productSchema/productTaxonomyUx'
import { db } from '../../../../../config/firebase'

const MAX_BATCH_WRITES = 400

export const BULK_TAXONOMY_MODE = {
  setMain: 'setMain',
  addExtra: 'addExtra',
  remove: 'remove',
  primaryCategorize: 'primaryCategorize',
}

export { partitionProductsForPrimaryCategorize }

function buildPatchFromNext(next, nodesById) {
  return buildProductTaxonomyWritePatch({
    assignmentIds: next.catalogTaxonomyAssignmentIds,
    previousCanonical: next.canonicalCatalogTaxonomyNodeId,
    nodesById,
    canonicalOverride: next.canonicalCatalogTaxonomyNodeId,
  })
}

/**
 * Mass categorize / re-categorize selected products.
 * - mainId set → set/replace main for all, then add extras
 * - keepExistingMain → do not change main; only add extras (products without main are skipped)
 */
export async function applyPrimaryBulkCategorization({
  products = [],
  mainId = null,
  extraIds = [],
  nodesById = {},
  keepExistingMain = false,
}) {
  const updated = []
  const skipped = []
  const failed = []
  const patches = []
  const extras = uniqueSortedIds(extraIds).filter((id) => id && id !== mainId)
  let replacedMain = 0

  const emptyResult = (extraSkipped = []) => ({
    updated,
    skipped: [...skipped, ...extraSkipped],
    failed,
    counts: {
      selected: products.length,
      updated: 0,
      skipped: skipped.length + extraSkipped.length,
      failed: 0,
      eligible: 0,
      replacedMain: 0,
    },
  })

  if (keepExistingMain) {
    if (!extras.length) {
      return emptyResult(
        products.map((p) => ({
          id: p.id,
          name: p.name,
          reason: 'Не обрано додаткових категорій',
        }))
      )
    }

    products.forEach((product) => {
      const taxonomy = readProductTaxonomyFields(product)
      const split = splitMainAndExtraAssignments(product)
      if (!split.hasMain) {
        skipped.push({
          id: product.id,
          name: product.name,
          reason: 'Без основної категорії',
        })
        return
      }

      let next = {
        catalogTaxonomyAssignmentIds: taxonomy.catalogTaxonomyAssignmentIds,
        canonicalCatalogTaxonomyNodeId: split.mainId,
      }

      extras.forEach((extraId) => {
        if (extraId === split.mainId) return
        const extraNode = nodesById[extraId]
        const ancestors = resolveAncestors(extraId, nodesById)
        const guard = assertCanAssignTaxonomyNode(extraNode, ancestors, nodesById)
        if (!guard.allowed) return
        const added = applyAddExtraCategory({
          assignmentIds: next.catalogTaxonomyAssignmentIds,
          previousCanonical: next.canonicalCatalogTaxonomyNodeId,
          extraId,
        })
        if (added.ok) next = added
      })

      const normalized = buildNormalizedTaxonomyState({
        assignmentIds: next.catalogTaxonomyAssignmentIds,
        previousCanonical: next.canonicalCatalogTaxonomyNodeId,
        nodesById,
      })

      const nextProduct = {
        ...product,
        ...normalized,
        canonicalCatalogTaxonomyNodeId: next.canonicalCatalogTaxonomyNodeId,
      }

      if (product.isActive === true) {
        const readiness = assertProductActivationReadiness(nextProduct, nodesById)
        if (!readiness.ok) {
          skipped.push({
            id: product.id,
            name: product.name,
            reason: readiness.blockers.map((b) => b.message).join(' '),
          })
          return
        }
      }

      patches.push({
        id: product.id,
        name: product.name,
        patch: buildPatchFromNext(
          {
            catalogTaxonomyAssignmentIds: normalized.catalogTaxonomyAssignmentIds,
            canonicalCatalogTaxonomyNodeId: next.canonicalCatalogTaxonomyNodeId,
          },
          nodesById
        ),
      })
    })
  } else {
    if (!mainId) {
      return emptyResult()
    }

    const mainNode = nodesById[mainId]
    const mainAncestors = resolveAncestors(mainId, nodesById)
    const mainGuard = assertCanAssignTaxonomyNode(mainNode, mainAncestors, nodesById)
    if (!mainGuard.allowed) {
      return {
        updated,
        skipped: products.map((p) => ({
          id: p.id,
          name: p.name,
          reason: mainGuard.message || 'Не можна призначити основну категорію',
        })),
        failed,
        counts: {
          selected: products.length,
          updated: 0,
          skipped: products.length,
          failed: 0,
          eligible: 0,
          replacedMain: 0,
        },
      }
    }

    products.forEach((product) => {
      const taxonomy = readProductTaxonomyFields(product)
      const split = splitMainAndExtraAssignments(product)
      if (split.hasMain && split.mainId !== mainId) {
        replacedMain += 1
      }

      let next = applySetMainCategory({
        assignmentIds: taxonomy.catalogTaxonomyAssignmentIds,
        previousCanonical: taxonomy.canonicalCatalogTaxonomyNodeId,
        mainId,
      })
      if (!next.ok) {
        skipped.push({ id: product.id, name: product.name, reason: next.message })
        return
      }

      extras.forEach((extraId) => {
        const extraNode = nodesById[extraId]
        const ancestors = resolveAncestors(extraId, nodesById)
        const guard = assertCanAssignTaxonomyNode(extraNode, ancestors, nodesById)
        if (!guard.allowed) return
        const added = applyAddExtraCategory({
          assignmentIds: next.catalogTaxonomyAssignmentIds,
          previousCanonical: next.canonicalCatalogTaxonomyNodeId,
          extraId,
        })
        if (added.ok) next = added
      })

      const normalized = buildNormalizedTaxonomyState({
        assignmentIds: next.catalogTaxonomyAssignmentIds,
        previousCanonical: next.canonicalCatalogTaxonomyNodeId,
        nodesById,
      })

      const nextProduct = {
        ...product,
        ...normalized,
        canonicalCatalogTaxonomyNodeId: next.canonicalCatalogTaxonomyNodeId,
      }

      if (product.isActive === true) {
        const readiness = assertProductActivationReadiness(nextProduct, nodesById)
        if (!readiness.ok) {
          skipped.push({
            id: product.id,
            name: product.name,
            reason: readiness.blockers.map((b) => b.message).join(' '),
          })
          return
        }
      }

      patches.push({
        id: product.id,
        name: product.name,
        patch: buildPatchFromNext(
          {
            catalogTaxonomyAssignmentIds: normalized.catalogTaxonomyAssignmentIds,
            canonicalCatalogTaxonomyNodeId: next.canonicalCatalogTaxonomyNodeId,
          },
          nodesById
        ),
      })
    })
  }

  for (let i = 0; i < patches.length; i += MAX_BATCH_WRITES) {
    const chunk = patches.slice(i, i + MAX_BATCH_WRITES)
    const batch = db.batch()
    chunk.forEach(({ id, patch }) => {
      batch.update(db.collection('products').doc(id), patch)
    })
    try {
      await batch.commit()
      chunk.forEach((item) => updated.push({ id: item.id, name: item.name }))
    } catch (error) {
      console.error(error)
      chunk.forEach((item) =>
        failed.push({
          id: item.id,
          name: item.name,
          reason: error.message || 'Помилка запису',
        })
      )
    }
  }

  return {
    updated,
    skipped,
    failed,
    counts: {
      selected: products.length,
      eligible: products.length,
      replacedMain,
      updated: updated.length,
      skipped: skipped.length,
      failed: failed.length,
    },
  }
}

/** @deprecated kept for transition / tests */
export async function applyBulkProductTaxonomyUpdates(args) {
  return applyPrimaryBulkCategorization({
    products: args.products || [],
    mainId: args.mainId || (args.nodeIds && args.nodeIds[0]) || null,
    extraIds: args.extraIds || (args.nodeIds || []).slice(1),
    nodesById: args.nodesById || {},
  })
}

export function countProductsWithMain(products = []) {
  return products.filter((product) => splitMainAndExtraAssignments(product).hasMain).length
}
